// src/components/entities/EntityDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchEntity,
  deleteEntity,
  toggleEntityStatus,
  fetchEntityProducts,
  fetchEntityAgencies,
  clearEntityError,
} from '../../store/actions/entityActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';
import Pagination from '../common/Pagination';

const EntityDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {  selectedEntity, loading, error, products, productsMeta, agencies, agenciesMeta } = useSelector(
    (state) => state.entities
  );

  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [agenciesPage, setAgenciesPage] = useState(1);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    toggleButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
    },
    toggleButtonHover: {
      backgroundColor: '#047857',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    entityImage: {
      width: '120px',
      height: '120px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      backgroundColor: '#e5e7eb',
    },
    entityIcon: {
      width: '120px',
      height: '120px',
      borderRadius: '0.5rem',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '3rem',
    },
    entityInfo: {
      flex: 1,
    },
    entityName: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    entityDescription: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '1rem',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    openBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    closedBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginTop: '1.5rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    tabContent: {
      padding: '2rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '2rem',
    },
    infoSection: {
      marginBottom: '2rem',
    },
    infoTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    infoList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    infoItem: {
      display: 'flex',
      padding: '0.75rem 0',
      borderBottom: '1px solid #f3f4f6',
    },
    infoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    infoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    productLink: {
      color: '#2563eb',
      textDecoration: 'none',
    },
    agencyLink: {
      color: '#2563eb',
      textDecoration: 'none',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchEntity(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (activeTab === 'products' && id) {
      dispatch(fetchEntityProducts(id, { page: productsPage, limit: 10 }));
    }
  }, [dispatch, id, activeTab, productsPage]);

  useEffect(() => {
    if (activeTab === 'agencies' && id) {
      dispatch(fetchEntityAgencies(id, { page: agenciesPage, limit: 10 }));
    }
  }, [dispatch, id, activeTab, agenciesPage]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteEntity(id));
      navigate('/admin/entities');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const handleToggleStatus = useCallback(async () => {
    try {
      await dispatch(toggleEntityStatus(id));
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  }, [dispatch, id]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!selectedEntity && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Entité non trouvée"
        />
        <Link to="/admin/entities" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Aperçu' },
    { id: 'products', label: `Produits (${selectedEntity?._count?.products || 0})` },
    { id: 'agencies', label: `Agences (${selectedEntity?._count?.agencies || 0})` },
    { id: 'documents', label: `Documents (${selectedEntity?._count?.documents || 0})` },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/entities')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails de l'entité</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/entities/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={handleToggleStatus}
            style={{
              ...styles.actionButton,
              ...styles.toggleButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButton.backgroundColor;
            }}
          >
            {selectedEntity?.isOpen ? (
              <>
                <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Fermer
              </>
            ) : (
              <>
                <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Ouvrir
              </>
            )}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearEntityError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            {selectedEntity.image ? (
              <img
                src={selectedEntity.image}
                alt={selectedEntity.name}
                style={styles.entityImage}
              />
            ) : (
              <div style={styles.entityIcon}>
                {selectedEntity.icon || '🏢'}
              </div>
            )}
            <div style={styles.entityInfo}>
              <h2 style={styles.entityName}>{selectedEntity.name}</h2>
              {selectedEntity.description && (
                <p style={styles.entityDescription}>{selectedEntity.description}</p>
              )}
              <div>
                <span
                  style={{
                    ...styles.badge,
                    ...(selectedEntity.isOpen ? styles.openBadge : styles.closedBadge),
                  }}
                >
                  {selectedEntity.isOpen ? 'Ouvert' : 'Fermé'}
                </span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Produits</div>
              <div style={styles.statValue}>{selectedEntity._count?.products || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Agences</div>
              <div style={styles.statValue}>{selectedEntity._count?.agencies || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Documents</div>
              <div style={styles.statValue}>{selectedEntity._count?.documents || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Note moyenne</div>
              <div style={styles.statValue}>
                {selectedEntity.rating > 0 ? selectedEntity.rating.toFixed(1) : 'N/A'}
                {selectedEntity.reviewCount > 0 && (
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                    ({selectedEntity.reviewCount} avis)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'overview' && (
            <div style={styles.infoGrid}>
              {/* Contact Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Contact</h3>
                <ul style={styles.infoList}>
                  {selectedEntity.phone && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Téléphone</span>
                      <span style={styles.infoValue}>
                        <a href={`tel:${selectedEntity.phone}`}>{selectedEntity.phone}</a>
                      </span>
                    </li>
                  )}
                  {selectedEntity.email && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Email</span>
                      <span style={styles.infoValue}>
                        <a href={`mailto:${selectedEntity.email}`}>{selectedEntity.email}</a>
                      </span>
                    </li>
                  )}
                  {selectedEntity.website && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Site web</span>
                      <span style={styles.infoValue}>
                        <a href={selectedEntity.website} target="_blank" rel="noopener noreferrer">
                          {selectedEntity.website}
                        </a>
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Location Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Localisation</h3>
                <ul style={styles.infoList}>
                  {selectedEntity.address && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Adresse</span>
                      <span style={styles.infoValue}>{selectedEntity.address}</span>
                    </li>
                  )}
                  {(selectedEntity.city || selectedEntity.state || selectedEntity.zipCode) && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Ville</span>
                      <span style={styles.infoValue}>
                        {[selectedEntity.city, selectedEntity.state, selectedEntity.zipCode].filter(Boolean).join(', ')}
                      </span>
                    </li>
                  )}
                  {(selectedEntity.lat || selectedEntity.long) && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Coordonnées</span>
                      <span style={styles.infoValue}>
                        {selectedEntity.lat && `Lat: ${selectedEntity.lat}`}
                        {selectedEntity.lat && selectedEntity.long && ', '}
                        {selectedEntity.long && `Long: ${selectedEntity.long}`}
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Additional Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Informations supplémentaires</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Ordre</span>
                    <span style={styles.infoValue}>{selectedEntity.order || 0}</span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Créé le</span>
                    <span style={styles.infoValue}>
                      {new Date(selectedEntity.createdAt).toLocaleDateString()}
                    </span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Modifié le</span>
                    <span style={styles.infoValue}>
                      {new Date(selectedEntity.updatedAt).toLocaleDateString()}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div>
              {products.length > 0 ? (
                <>
                  <table style={styles.table}>
                    <thead style={styles.tableHeader}>
                      <tr>
                        <th style={styles.tableHeaderCell}>Produit</th>
                        <th style={styles.tableHeaderCell}>Description</th>
                        <th style={styles.tableHeaderCell}>Statut</th>
                        <th style={styles.tableHeaderCell}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.id} style={styles.tableRow}>
                          <td style={styles.tableCell}>
                            <Link to={`/admin/products/${product.id}/details`} style={styles.productLink}>
                              {product.name}
                            </Link>
                          </td>
                          <td style={styles.tableCell}>{product.description}</td>
                          <td style={styles.tableCell}>
                            <span
                              style={{
                                ...styles.badge,
                                ...(product.isActive ? styles.openBadge : styles.closedBadge),
                              }}
                            >
                              {product.isActive ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <Link to={`/admin/products/${product.id}/details`}>
                              Voir
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={productsMeta.page}
                    totalPages={productsMeta.totalPages}
                    onPageChange={setProductsPage}
                  />
                </>
              ) : (
                <div style={styles.emptyState}>
                  Aucun produit trouvé pour cette entité
                </div>
              )}
            </div>
          )}

          {activeTab === 'agencies' && (
            <div>
              {agencies.length > 0 ? (
                <>
                  <table style={styles.table}>
                    <thead style={styles.tableHeader}>
                      <tr>
                        <th style={styles.tableHeaderCell}>Agence</th>
                        <th style={styles.tableHeaderCell}>Adresse</th>
                        <th style={styles.tableHeaderCell}>Contact</th>
                        <th style={styles.tableHeaderCell}>Distance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencies.map((agency) => (
                        <tr key={agency.id} style={styles.tableRow}>
                          <td style={styles.tableCell}>
                            <Link to={`/admin/agencies/${agency.id}/details`} style={styles.agencyLink}>
                              {agency.name}
                            </Link>
                          </td>
                          <td style={styles.tableCell}>
                            {[agency.address, agency.city, agency.zipCode].filter(Boolean).join(', ')}
                          </td>
                          <td style={styles.tableCell}>
                            {agency.phone && <div>{agency.phone}</div>}
                            {agency.email && <div>{agency.email}</div>}
                          </td>
                          <td style={styles.tableCell}>
                            {agency.distance ? `${agency.distance.toFixed(1)} km` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={agenciesMeta.page}
                    totalPages={agenciesMeta.totalPages}
                    onPageChange={setAgenciesPage}
                  />
                </>
              ) : (
                <div style={styles.emptyState}>
                  Aucune agence trouvée pour cette entité
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div style={styles.emptyState}>
              Section documents en cours de développement
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDelete}
        title="Supprimer l'entité"
        message={`Êtes-vous sûr de vouloir supprimer l'entité "${selectedEntity?.name}" ? Cette action supprimera également tous les produits, agences et documents associés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default EntityDetails;